import {
  COMMON_SAVE_FORM_DATA_BEGIN,
  COMMON_SAVE_FORM_DATA_REJECT,
  COMMON_SAVE_FORM_DATA_SUCCESS,
  COMMON_SAVE_FORM_DATA_FAILURE,
  COMMON_SAVE_FORM_DATA_DISMISS_ERROR,
} from './constants'

import _ from 'lodash/fp'
import axios from 'axios'
import { omittedFieldsForSaveMaestro, omittedFieldsForSaveProducto, omittedFieldsForSaveStock } from '../formulario/omittedFieldsForSave'
import { difference, validateVin } from '../Utilities'
import { createFileFormData } from 'common/utils/utils'
import { getEndpoint } from '../backend/endpoints'
import validatePatente from '../../common/backend/validatePatente'
import { NEW_ITEM_ID } from '../formulario/constants'
import { STOCKS_ENDPOINT } from 'cfgCommon/endpoints'

const AÑO_ACTUAL = new Date().getFullYear()
// cada filtro consiste de un predicado que da true/false y un mensaje
// para mostrar en el caso de que no pase el filtro
const filtrosProducto = [
  {
    filtro: ({ precio_lista = 0, flete_alistaje = 0, bonificacion = 0, nuevo }) => {
      if(nuevo) {
        return (
          Number(precio_lista) + Number(flete_alistaje) - Number(bonificacion) > 0
        )
      } else {
        return Number(precio_lista) > 0
      }
    },
    message: 'El precio de venta debe ser mayor a 0'
  },
  {
    filtro: ({ nuevo, patente }) => {
      if (!nuevo) {
        return validatePatente(patente)
      }
      return true
    },
    message: 'La patente no es correcta'
  },
  {
    filtro: ({ venta_convencional, venta_directa, nuevo }) =>
      nuevo ? venta_convencional || venta_directa : true,
    message: 'Al menos uno de los tipos de venta tiene que ser "Si"'
  },
  {
    filtro: ({ colors }) => colors?.length,
    message: 'Debe seleccionar un color',
  },
  {
    filtro: ({ colors }) => colors?.length ? colors.every(({name})=> name !== '') : true,
    message: 'El nombre del color es obligatorio',
  },
  {
    filtro: ({ nuevo, kilometros }) => { 
      if(!nuevo ) return kilometros > 0
      else return true
    },
    message: 'El kilometraje debe ser mayor a 0.',
  },
  {
    filtro: ({ nuevo, location_id }) => { 
      if(!nuevo ) return Boolean(location_id)
        else return true
    },
    message: 'Debe seleccionar una ubicación.',
  },
]

const filtrosMaestro = [
  {
    filtro: ({ brand }) => Boolean(brand),
    message: 'Hay que elegir marca',
  },
  {
    filtro: ({ model }) => Boolean(model),
    message: 'Hay que elegir modelo',
  },
  {
    filtro: ({ version }) => Boolean(version),
    message: 'Hay que elegir version',
  },
  {
    filtro: ({ SEGMENTO }) => Boolean(SEGMENTO),
    message: 'Hay que elegir segmento',
  },
  {
    filtro: ({ origen }) => Boolean(origen),
    message: 'Hay que elegir pais de origen',
  },
  {
    filtro: ({ DESDE }) => Number(DESDE) >= 1900,
    message: 'Hay que ingresar año desde, y tiene que ser mayor a 1900',
  },
  {
    filtro: ({ HASTA = AÑO_ACTUAL }) => {
      if (Number(HASTA) === 0) {
        return true
      }
      return Number(HASTA) <= Number(AÑO_ACTUAL)
    },
    message: 'Hay que ingresar año hasta, y tiene que ser menor al año actual',
  },
  {
    // si HASTA es 0 o no existe, se considera como año actual, y sería un valor válido
    filtro: ({ DESDE, HASTA = AÑO_ACTUAL }) => {
      if (Number(HASTA) === 0) {
        return true
      } else {
        return Number(HASTA) >= Number(DESDE)
      }
    },
    message: 'El año hasta tiene que ser mayor al año desde',
  },
]

const filtrosStock = [
  {
    filtro: ({ owner_id, nuevo }) => nuevo ? Boolean(owner_id) : true,
    message: 'Hay que elegir dueño del producto',
  },
  {
    filtro: ({ idColor, colors, nuevo }) => nuevo ? (colors.length === 0 ? true : Boolean(idColor)) : true,
    message: 'Debe seleccionar un color',
  },
  {
    filtro: ({ location_id, branch_office_id }) => branch_office_id && Boolean(location_id),
    message: 'Hay que elegir una ubicación',
  },
  {
    filtro: ({ vin, nuevo, disponible }) => {
      // permitir vin vacío para VH usados
      if(!nuevo && !vin) {
        return true
      }
      // permitir vin vacío para VH nuevos y que no sean FISICO
      if(nuevo && disponible !== "FISICO" && !vin) {
        return true
      }
      return validateVin(vin)
    },
    message: ({ vin, nuevo, disponible }) => !vin && nuevo && disponible === 'FISICO' 
                                            ? 'Debe asignar un VIN al stock' 
                                            : 'El vin debe tener 17 caracteres alfanuméricos',
  },
]

// usar con metodo _.mapValues() de lodash
const filtrarFlagDeleted = (value) => {
  // en modo crear no mando los objetos que tienen flag deleted
  // pero en modo edición si necesito mandar esa flag
  // FIXME: este metodo no recursa, solo compara arrays en el primer nivel
  // con objetos en el segundo nivel que tienen un flag 'deleted'
  if (_.isArray(value)) {
    return value.filter((e) => !e.deleted)
  }
  return value
}

// en modo edicion solo conservo los campos que han cambiado
// en ambos casos filtro los campos listados en omittedFieldsForSave
function getObjectToSave(oldObject, newObject, mode, omittedFieldsForSave) {
  if (mode === 'PUT') {
    return _.flow(
      _.omit(omittedFieldsForSave),
      _.mapValues((value) => {
        if (value && value.label && value.value) {
          return value.value
        } else {
          if(Array.isArray(value) && value.findIndex(colors => colors.color) >= 0) { 
            // devuelvo el id de color asofix dentro del array de colors
            const colors = value.map(item => {
              if(item.catalog_asofix_color_id) {
                item.catalog_asofix_color_id = item.catalog_asofix_color_id.value
                return item
              }
              return item
            })
            return colors
          }
          return value
        }
      }),
      _.pickBy((value, key) => !_.isEqual(value, oldObject[key])),
        /* Se envía el parámetro mediante una condicional con la finalidad de
         * tener mayor control sobre el valor obtenido a través de oldObject
         * Esto al desconocerse los posibles valores que pueda contener el mismo
         * A su vez esto permite al backend tener en cuenta que siempre recibirá
         * null en caso el valor no se encuentre definido correctamente.
         * FIXME: eliminar el ternario con null. enviar los parametros
         * todos los parámetros definidos acá son los que deberían (y son)
         * obligatorios por diversas razones. El back end debería dar un error
         * en el caso de que no estén. Y NUNCA deberían enviarse como null
         * excepto tal vez el caso de branch_office_id hasta que se corriga eso
         */
      _.defaults({
        branch_office_id: oldObject.branch_office_id
          ? oldObject.branch_office_id
          : null,
        datamaster_id: oldObject.datamaster_id ? oldObject.datamaster_id : null,
        car_lot_id: oldObject.car_lot_id ? oldObject.car_lot_id : null,
        nuevo: oldObject.nuevo,
        codigo: oldObject.CODIGO,
        shareCarmuvSAS: oldObject.shareCarmuvSAS,
      })
    )(newObject)
  } else {
    return _.flow(
      _.omit(omittedFieldsForSave),
      _.mapValues((value) => {
        if (value && value.label && value.value) {
          return value.value
        } else {
          if(Array.isArray(value) && value.findIndex(colors => colors.color) >= 0) { 
            // devuelvo el id de color asofix dentro del array de colors
            const colors = value.map(item => {
              if(item.catalog_asofix_color_id) {
                item.catalog_asofix_color_id = item.catalog_asofix_color_id.value
                return item
              }
              return item
            })
            return colors
          }
          return value
        }
      }),
      _.mapValues(filtrarFlagDeleted),
    )(newObject)
  }
}

export function saveFormData({ formState, mode, source, files } = {}) {
  return (dispatch, getState) => {

    let omittedFieldsForSave
    if(source === 'MAESTRO') {
      omittedFieldsForSave = omittedFieldsForSaveMaestro
    } else if(source === 'PRODUCTO') {
      omittedFieldsForSave = omittedFieldsForSaveProducto
    } else {
      omittedFieldsForSave = omittedFieldsForSaveStock
    }

    const state = getState()
    const objectToSave = getObjectToSave(state.common.formData.itemData, formState, mode, omittedFieldsForSave)
    const itemId = formState.id
    const endpoint = getEndpoint(source)

    // if es estado borrador? (si), permito que lo guarde, 
    // es publicado (si), con todos los demas campos obligatorios 
    // se separan en nuevo o usado los filtros
    let filtros = []
    if(source === 'MAESTRO') {
      filtros = filtrosMaestro
    } else if(source === 'PRODUCTO' ) {
      filtros = filtrosProducto
    } else {
      filtros = filtrosStock
    }
     
    const incorrectValues = filtros
      .filter(({ filtro }) => !filtro(formState))
      .map(({ message }) => {
        if (typeof message === 'function') {
          return message(formState)
        } else {
          return message
        }
      })

    if (incorrectValues.length > 0) {
      const message = incorrectValues
      const typeMessage = 'error'
      dispatch({
        type: COMMON_SAVE_FORM_DATA_REJECT,
        data: {
          message,
          typeMessage,
        },
      })
      return Promise.reject({ message, typeMessage })
    }

    // const __omitted = ISPOST ? omitedPost : omitedPut

    const body = _.omit(omittedFieldsForSave)(objectToSave)
    if(body.id === NEW_ITEM_ID) {
      delete body.id
    }
    
    // chequeo de validación
    // si es usado, eliminar bonificación de fábrica porque no corresponde
    if(!body.nuevo) {
      delete body.bonificacion
    }
    
    // send empty strings as nulls
    Object.entries(body).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim().length === 0) {
        body[key] = null
      }
    })

    dispatch({
      type: COMMON_SAVE_FORM_DATA_BEGIN,
      data: {
        formState,
        objectSent: body,
        mode,
        source,
      },
    })
    const promise = new Promise((resolve, reject) => {
      if (mode !== 'POST' && mode !== 'PUT') {
        reject(new Error('Los metodos posibles son PUT y POST'))
      }

      const location_id = body?.location_id
      if (source === 'PRODUCTO')
        delete body.location_id

      const bodyParsed = source === "PRODUCTO" ? createFileFormData(files, body) : body
      const timeout = { timeout: 240000 } // 4 minutos

      const doRequest = mode === 'POST' ? axios.post(`${endpoint}/`, bodyParsed, timeout) : axios.put(`${endpoint}/${itemId}`, bodyParsed, timeout)
      doRequest.then(
        (res) => {
          // busca la data en res.data.data, si no existe asume que está en res.data
          let resolveData = res.data.data
          if (!res.data.data) {
            resolveData = res.data
          }

          if (source === 'PRODUCTO' && location_id && !body.nuevo) {
            const stockId = resolveData.Stocks[0].id
            axios.put(`${STOCKS_ENDPOINT}/${stockId}`,
            {
              location_id,
              estado_anterior: resolveData.Stocks[0].estado,
            })
          }

          dispatch({
            type: COMMON_SAVE_FORM_DATA_SUCCESS,
            data: {
              saveFormDataResponse: res,
            },
          })

          const diff = difference(body, resolveData)
          Object.entries(diff).forEach(([key]) => {
            if (!isNaN(body[key]) || !isNaN(resolveData[key])) {
              if (Number(body[key]) !== Number(resolveData[key]))
                console.warn('SAVE DIFF', `${key} SENT: ${body[key]} RECEIVED: ${resolveData[key]}`)
            } else {
              console.warn('SAVE DIFF', `${key} SENT: ${body[key]} RECEIVED: ${resolveData[key]}`)
            }
          })
          resolve(resolveData)
        },
        // Use rejectHandler as the second argument so that render errors won't be caught.
        (err) => {
          dispatch({
            type: COMMON_SAVE_FORM_DATA_FAILURE,
            data: { error: err },
          })
          reject(err)
        },
      )
    })

    return promise
  }
}

// Async action saves request error by default, this method is used to dismiss the error info.
// If you don't want errors to be saved in Redux store, just ignore this method.
export function dismissSaveFormDataError() {
  return {
    type: COMMON_SAVE_FORM_DATA_DISMISS_ERROR,
  }
}

export function reducer(state, action) {
  switch (action.type) {
    case COMMON_SAVE_FORM_DATA_BEGIN:
      // Just after a request is sent
      return {
        ...state,
        saveFormDataPending: true,
        saveFormDataError: null,
        formData: {
          ...state.formData,
          ...action.data,
        },
      }

    case COMMON_SAVE_FORM_DATA_SUCCESS:
      // The request is success
      return {
        ...state,
        saveFormDataPending: false,
        saveFormDataError: null,
        formData: {
          ...state.formData,
          ...action.data,
        },
      }

    case COMMON_SAVE_FORM_DATA_REJECT:
      // The request is success
      return {
        ...state,
        saveFormDataPending: false,
        saveFormDataError: null,
        formData: {
          ...state.formData,
          ...action.data,
        },
      }

    case COMMON_SAVE_FORM_DATA_FAILURE:
      // The request is failed
      return {
        ...state,
        saveFormDataPending: false,
        formData: {
          ...state.formData,
          itemResponse: action.data.itemResponse,
        },
        saveFormDataError: action.data.error,
      }

    case COMMON_SAVE_FORM_DATA_DISMISS_ERROR:
      // Dismiss the request failure error
      return {
        ...state,
        saveFormDataError: null,
      }

    default:
      return state
  }
}
